/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import _ from 'lodash';
import urlJoin from 'proper-url-join';

import { endpoints } from '@ibm-watson/shared/modules/ia/util/config';
import { validation } from '@ibm-watson/shared/modules/ia/util/validation';
import { getAccessPrefixedUrl } from '../../../ia/util/access-control';

const assistantVariablesEndpoint = `${endpoints.store}/v2/system_variables`;

const variablesEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): string =>
  urlJoin(
    endpoints.store,
    getAccessPrefixedUrl(
      useNewIAUpdatedAccess,
      assistantId,
      `skills/${skillId}/workspace/variables`
    )
  );

const variableEndpoint = (
  skillId: string,
  variableId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): string =>
  `${variablesEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId
  )}/${variableId}`;

const auditParams = {
  include_audit: true
};

const validateAll = skillId => {
  var validationErrors = [];

  if (skillId) {
    const skillErrors = validation.validateGuid(skillId);
    if (skillErrors) {
      validationErrors.push(skillErrors);
    }
  }

  // TBD validate variable

  if (validationErrors.length) {
    return Promise.reject({ data: { error: validationErrors } });
  }

  return null;
};

// POST v2/skills/<skill_id>/workspace/variables : Create skill variable
export async function createVariable(
  skillId: string,
  variable: any,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) {
  // validation of variable not available
  // const validationErrors = validateAll(skillId, variable);
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }

  const url = variablesEndpoint(skillId, useNewIAUpdatedAccess, assistantId);

  return axios
    .post(url, variable, { params: auditParams })
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
}

// DELETE v2/skills/<skill_id>/workspace/variables/<variable_name> : Delete skill variable
export async function deleteVariable(
  skillId: string,
  variableId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) {
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }

  return axios
    .delete(
      variableEndpoint(skillId, variableId, useNewIAUpdatedAccess, assistantId)
    )
    .then(response => response)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
}

// GET v2/skills/<skill_id>/workspace/variables/<variable_name>
export async function getVariable(
  skillId: string,
  variableId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  options: any = {}
) {
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }

  return axios
    .get(
      variableEndpoint(skillId, variableId, useNewIAUpdatedAccess, assistantId),
      {
        params: { ...auditParams, ...options }
      }
    )
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
}

// GET v2/skills/<skill_id>/workspace/variables
export async function listVariables(
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  reactivateSession: any
) {
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }

  const params = {
    page_limit: 1000,
    include_count: true,
    include_audit: true,
    include_action_references: true,
    sort: 'variable'
  };

  const config = { params };

  return axios
    .get(
      urlJoin(variablesEndpoint(skillId, useNewIAUpdatedAccess, assistantId), {
        query: {
          reactivateSession
        }
      }),
      config
    )
    .then(response => {
      return {
        variables: response.data.variables,
        total: response.data.pagination.total,
        nextURL: response.data.pagination.next_url
          ? response.data.pagination.next_url
          : ''
      };
    })
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
}

// GET v2/system_variables
export async function listSystemVariables(userParams) {
  const params = {
    page_limit: 1000,
    include_count: true,
    include_audit: true,
    language: 'en',
    ...userParams
  };

  const config = { params };

  return axios
    .get(assistantVariablesEndpoint, config)
    .then(response =>
      response?.data?.filter(
        variable => variable.system_variable !== 'last_action'
      )
    )
    .catch(error => Promise.reject(_.get(error, 'response.data', error)));
}

// POST v2/skills/<skill_id>/workspace/variables/<variable_name>
export async function updateVariable(
  skillId: string,
  variableId: string,
  newVariable: any,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) {
  // validation of variable not available
  // const validationErrors = validateAll(skillId, newVariable);
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }

  return axios
    .post(
      variableEndpoint(skillId, variableId, useNewIAUpdatedAccess, assistantId),
      newVariable,
      {
        params: auditParams
      }
    )
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data', error)));
}
